/**
 * CMSBodyRenderer — renders a CMS page body string as React elements.
 *
 * Supported embed shortcodes (paste into any CMS page body):
 *   <div data-smbprime-embed="faq-section"></div>
 *
 * Strategy Call CTA (opens Book a Strategy Call modal from CMS content):
 *   <a href="#open-strategy-call">Book a Strategy Call</a>
 *   <button type="button" data-action="open-strategy-call">Book a Strategy Call</button>
 *
 * The body is split on embed markers; each segment is rendered either as
 * raw HTML (prose) or as the matching React component.
 */

import { FAQSection } from './FAQSection';
import { BlogArticlesEmbed } from './BlogArticlesEmbed';

/**
 * Match embed markers in any of their stored forms:
 *   <div data-smbprime-embed="faq-section"></div>   (literal quotes)
 *   <div data-smbprime-embed=&quot;faq-section&quot;></div>  (HTML-entity quotes from Monaco)
 *   <div data-smbprime-embed='faq-section'></div>   (single quotes)
 *
 * We do NOT decode the whole body string — we match entity forms directly so the
 * original body (with &quot; etc.) is always passed to dangerouslySetInnerHTML
 * untouched. The browser decodes entities inside attribute values correctly on
 * its own, so style="url(&quot;/img.png&quot;)" renders fine.
 */
const EMBED_PATTERN =
  /<div[^>]*data-smbprime-embed\s*=\s*(?:&quot;|["'])([^"'&\s]+)(?:&quot;|["'])[^>]*(?:\/>|>\s*<\s*\/\s*div\s*>)/gi;

interface Segment {
  type: 'html' | 'embed';
  content: string;
}

function parseBody(body: string): Segment[] {
  const segments: Segment[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  const regex = new RegExp(EMBED_PATTERN.source, 'gi');
  while ((match = regex.exec(body)) !== null) {
    if (match.index > lastIndex) {
      segments.push({ type: 'html', content: body.slice(lastIndex, match.index) });
    }
    segments.push({ type: 'embed', content: match[1].trim() });
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < body.length) {
    segments.push({ type: 'html', content: body.slice(lastIndex) });
  }

  return segments;
}

function renderEmbed(embedId: string, key: number): JSX.Element | null {
  switch (embedId) {
    case 'faq-section':
      return <FAQSection key={key} />;
    case 'blog-articles':
      return <BlogArticlesEmbed key={key} />;
    case 'blog-articles-featured':
      return <BlogArticlesEmbed key={key} variant="featured" />;
    default:
      console.warn(`[CMSBodyRenderer] Unknown embed id "${embedId}" — skipping.`);
      return null;
  }
}

interface CMSBodyRendererProps {
  body: string;
  className?: string;
}

export function CMSBodyRenderer({ body, className }: CMSBodyRendererProps): JSX.Element {
  if (!body) return <></>;

  const segments = parseBody(body);

  // Fast-path: no embeds → render the original body; the browser decodes HTML entities correctly
  if (segments.length === 1 && segments[0].type === 'html') {
    return (
      <div
        className={className ?? 'prose prose-gray max-w-none'}
        dangerouslySetInnerHTML={{ __html: body }}
      />
    );
  }

  return (
    <>
      {segments.map((seg, i) => {
        if (seg.type === 'embed') {
          return renderEmbed(seg.content, i);
        }
        if (!seg.content.trim()) return null;
        return (
          <div
            key={i}
            className={className ?? 'prose prose-gray max-w-none'}
            dangerouslySetInnerHTML={{ __html: seg.content }}
          />
        );
      })}
    </>
  );
}
